import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { ArrowLeft, ArrowRight, ArrowUp, Reset } from '@factorialco/f0-react/icons/app'
import { motion } from 'framer-motion'

import i18n from 'modules/core/lib/i18n'

import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import {
  navigationButtons,
  navigationContainer,
} from 'modules/trainings/components/content/styles.vanilla.css'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { PageBlock } from 'modules/trainings/lib/content/PageBlock'
import { ConsumerCompletedState } from 'modules/trainings/pages/content/ConsumerCompletedState'

const contentAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.25, ease: 'easeOut' },
}

const SCROLL_THRESHOLD = 50

interface ContentConsumerProps {
  selectedItem?: ContentItem
  showEmptyState: boolean
  onBack?: () => void
  onNext?: () => void
  onRestart?: () => void
}

const getNextButtonIcon = (label: string) => {
  if (label === i18n.t('trainings.content.consumer.quiz.retry')) return Reset
  if (label === i18n.t('common.submit')) return ArrowUp
  return ArrowRight
}

const ContentConsumer = forwardRef(function ContentConsumer(
  { selectedItem, showEmptyState, onBack, onNext, onRestart }: ContentConsumerProps,
  ref: Ref<ContentConsumerCallbacks>
) {
  const innerRef = useRef<ContentConsumerCallbacks>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [nextButtonLabel, setNextButtonLabel] = useState('')
  const [nextButtonEnabled, setNextButtonEnabled] = useState(true)
  const [nextButtonLoading, setNextButtonLoading] = useState(false)
  const [needsScrollToBottom, setNeedsScrollToBottom] = useState(false)

  const isUncompletedPageBlock =
    selectedItem instanceof PageBlock && selectedItem.status !== 'completed'
  const showNavigation = !showEmptyState && onNext
  const nextButtonIcon = getNextButtonIcon(nextButtonLabel)
  const isNextDisabled = !nextButtonEnabled || needsScrollToBottom
  const isBackDisabled = !onBack

  useImperativeHandle(
    ref,
    () => ({
      handleNext: async () => innerRef.current?.handleNext() ?? true,
      nextEnabled: () => innerRef.current?.nextEnabled() ?? true,
      nextLabel: () => innerRef.current?.nextLabel() ?? '',
      nextLoading: () => innerRef.current?.nextLoading?.() ?? false,
    }),
    []
  )

  const updateNextButton = useCallback(() => {
    const current = innerRef.current
    setNextButtonLabel(current?.nextLabel() || i18n.t('common.next'))
    setNextButtonEnabled(current?.nextEnabled() ?? true)
    setNextButtonLoading(current?.nextLoading?.() ?? false)
  }, [])

  useEffect(() => {
    updateNextButton()
  }, [selectedItem, updateNextButton])

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 })
    setNeedsScrollToBottom(false)
  }, [selectedItem])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !isUncompletedPageBlock) return

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const scrollNeeded = scrollHeight > clientHeight
      const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD
      setNeedsScrollToBottom(scrollNeeded && !atBottom)
    }

    container.addEventListener('scroll', checkScroll, { passive: true })
    requestAnimationFrame(() => requestAnimationFrame(checkScroll))

    return () => container.removeEventListener('scroll', checkScroll)
  }, [isUncompletedPageBlock, selectedItem])

  return (
    <Box height="full" position="relative" flexDirection="column">
      <Box ref={scrollContainerRef} flexGrow overflowY="auto" position="relative" paddingTop="s24">
        {showEmptyState || !selectedItem ? (
          <Box alignItems="center" justifyContent="center" height="full">
            <ConsumerCompletedState onRestart={onRestart} />
          </Box>
        ) : selectedItem instanceof ContentModule ? (
          <RenderItem item={selectedItem} updateNextButton={updateNextButton} ref={innerRef} />
        ) : (
          <motion.div
            key={selectedItem.uuid}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={contentAnimation}
            transition={contentAnimation.transition}
          >
            <Box alignItems="center" paddingBottom="s80">
              <RenderItem item={selectedItem} updateNextButton={updateNextButton} ref={innerRef} />
            </Box>
          </motion.div>
        )}
      </Box>

      {showNavigation && (
        <Box
          position="absolute"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          paddingY="s16"
          className={navigationContainer}
        >
          <Box flexDirection="row" gap="s16" className={navigationButtons}>
            <Box
              background={isBackDisabled ? 'grey800' : 'grey000White'}
              borderRadius={{ all: 'abs012' }}
            >
              <Button
                icon={ArrowLeft}
                label={i18n.t('trainings.content.consumer.back')}
                variant="outline"
                disabled={isBackDisabled}
                onClick={onBack}
              />
            </Box>
            <Box background="grey000White" borderRadius={{ all: 'abs012' }}>
              <Button
                icon={nextButtonIcon}
                label={nextButtonLabel}
                disabled={isNextDisabled}
                loading={nextButtonLoading}
                onClick={onNext}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
})

const RenderItem = forwardRef(function RenderItem(
  { item, updateNextButton }: { item: ContentItem; updateNextButton: () => void },
  ref: Ref<ContentConsumerCallbacks>
) {
  const ConsumerComponent = contentRegistry.fetch(item, 'consumerComponent')
  if (!ConsumerComponent) return null

  return (
    <ConsumerComponent
      contentItem={item}
      updateNextButton={updateNextButton}
      ref={ref}
      isFullscreen
    />
  )
})

export default ContentConsumer
